import React, { useState, useMemo } from 'react';
import { apis, hooks, models } from "@vote4dance/judging-sdk";
import { App, Button, Dropdown, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { _T } from "../../../../../base-react/src/Localization";
var Api = apis.Api;
var Stream = hooks.streams.Stream;
var ClassTeamSource = models.teams.ClassTeamSource;
var TeamSource = models.teams.TeamSource;
var TeamItem = models.teams.TeamItem;
var CompetitionLevel = models.competitions.CompetitionLevel;
import { TeamEdit } from './TeamEdit';
import { CommentOutlined, DeleteOutlined, DownOutlined, PrinterOutlined, StopOutlined, UserAddOutlined, WarningOutlined } from '@ant-design/icons';
import { ButtonConfirm } from '../../../../../base-react/components/ButtonConfirm';
import { Link } from 'react-router-dom';
import { AssignStartNumbers } from './AssignStartNumbers';
import { ExternalAPI } from '../../../../components/ExternalAPI';
import { useCompetition } from '../../../../../base-react/hooks/Competition';
import { GenerateRandomTeams } from './GenerateRandomTeams';
import { TeamImagePreviewButton } from '../../../../../base-react/components/TeamImagePreviewButton';
const { Link: LinkText } = Typography;
export const TeamsTable = (props) => {
    const { event_id, competition_id, classItem, disabled } = props;
    const [teams, loading] = Stream(ClassTeamSource(classItem.class_id));
    const usingParentTeams = teams && teams.some(team => parseInt(team.team_parent_id) > 0);
    const [parentTeams, loadingParentTeams] = Stream((classItem && usingParentTeams && parseInt(classItem.class_group_id) > 0) ? ClassTeamSource(classItem.class_group_id) : undefined);
    const [visibleEdit, setVisibleEdit] = useState(false);
    const [editedTeam, setEditedTeam] = useState();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const usingCountry = teams && teams.map(team => team.team_country).join('').length > 0;
    const [competition] = useCompetition(competition_id);
    const isPractice = useMemo(() => {
        return (competition === null || competition === void 0 ? void 0 : competition.competition_level) === CompetitionLevel.Practice;
    }, [competition]);
    const [visibleGenerate, setVisibleGenerate] = useState(false);
    const { notification: notificationApi } = App.useApp();
    const showTeam = (event, team) => {
        event.stopPropagation();
        setVisibleEdit(true);
        setEditedTeam(team);
    };
    const closeTeam = () => {
        setVisibleEdit(false);
        setEditedTeam(undefined);
    };
    const duplicateTeam = (team) => {
        const duplicatedTeam = {
            ...team,
            class_id: undefined,
            team_id: undefined,
            team_class: undefined,
            team_placement: '',
            team_sum: ''
        };
        setEditedTeam(duplicatedTeam);
        setVisibleEdit(true);
    };
    const getTeamSortNumber = (team) => {
        if (team.team_placement == '')
            return -2;
        if (isNaN(parseInt(team.team_placement)))
            return -1;
        return parseInt(team.team_placement);
    };
    const columns = [
        {
            title: _T('team.details.team_placement.short'),
            dataIndex: 'team_placement',
            responsive: ['md'],
            sorter: (a, b) => getTeamSortNumber(a) - getTeamSortNumber(b)
        },
        {
            title: _T('team.details.team_start_number.short'),
            dataIndex: 'team_start_number',
            sorter: (a, b) => a.team_start_number - b.team_start_number,
        },
        {
            dataIndex: "team_name",
            title: _T('team.details.team_name.label'),
            render: (text, item) => React.createElement(Space, null,
                React.createElement(Button, { block: true, type: "link", onClick: (e) => showTeam(e, item), style: { padding: 0 } },
                    React.createElement(LinkText, { key: `team-${item.team_id}-${item.team_cancelled}`, delete: item.team_cancelled > 0 }, TeamItem.getFullName(item) ? TeamItem.getFullName(item) : _T('common.empty'))),
                React.createElement(TeamImagePreviewButton, { imageId: item.team_image_id }),
                item.team_disqualified > 0 &&
                    React.createElement(Tooltip, { title: _T('team.details.team_disqualified.label') },
                        React.createElement(StopOutlined, null)),
                item.team_warning &&
                    React.createElement(Tooltip, { title: item.team_warning },
                        React.createElement(WarningOutlined, null)),
                item.team_note &&
                    React.createElement(Tooltip, { title: item.team_note },
                        React.createElement(CommentOutlined, null)))
        },
        {
            title: _T('team.details.team_org.label'),
            dataIndex: 'team_org',
            responsive: ['lg'],
            sorter: (a, b) => (a.team_org ? a.team_org : '') < (b.team_org ? b.team_org : '') ? -1 : 1,
        },
        usingCountry ?
            {
                title: _T('team.details.team_country.label'),
                dataIndex: 'team_country',
                responsive: ['lg'],
                sorter: (a, b) => (a.team_country ? a.team_country : '') < (b.team_country ? b.team_country : '') ? -1 : 1,
            } : {},
        classItem && usingParentTeams && parseInt(classItem.class_group_id) > 0 ?
            {
                title: _T('team.details.team_parent_id.label'),
                dataIndex: 'team_parent_id',
                responsive: ['lg'],
                render: (text, item) => {
                    if (loadingParentTeams)
                        return React.createElement(Spin, null);
                    const foundParentTeam = parentTeams === null || parentTeams === void 0 ? void 0 : parentTeams.find(a => a.team_id === item.team_parent_id);
                    return foundParentTeam && TeamItem.getFullName(foundParentTeam);
                }
            } : {},
        {
            key: 'print',
            responsive: ['lg'],
            render: (text, item) => React.createElement(Link, { target: "_blank", to: `/print/${competition_id}/startnumber/${item.team_start_number}` },
                React.createElement(Button, { icon: React.createElement(PrinterOutlined, null) }))
        },
    ].filter(column => Object.keys(column).length > 0);
    const rowSelection = {
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
        }
    };
    const deleteSelectedTeams = async (done) => {
        for (let key of selectedRowKeys) {
            await Api.destroy(`${TeamSource(competition_id).url}/${key}`)
                .then(() => {
                notificationApi.success({
                    title: _T('common.delete.success', { name: _T('team.title').toLowerCase() })
                });
            })
                .catch((err) => {
                var _a;
                notificationApi.error({
                    title: ((_a = err.response) === null || _a === void 0 ? void 0 : _a.message) ? _T(err.response.message) : err.message
                });
            });
        }
        done();
    };
    const onMenuClick = (e) => {
        if (e.key === 'generate') {
            if (!isPractice) {
                return;
            }
            setVisibleGenerate(true);
        }
    };
    const items = isPractice ? [
        { key: 'generate', label: _T('team.generate'), icon: React.createElement(UserAddOutlined, null) }
    ] : [];
    const hasMenu = items.length > 0;
    const registerDisabled = disabled || classItem.class_id == 'orphan';
    const RegisterButton = hasMenu ? (React.createElement(Space.Compact, null,
        React.createElement(Button, { type: "primary", disabled: registerDisabled, onClick: () => setVisibleEdit(true), icon: React.createElement(UserAddOutlined, null) }, _T('team.register')),
        React.createElement(Dropdown, { menu: { items, onClick: onMenuClick }, trigger: ['click'], disabled: registerDisabled },
            React.createElement(Button, { type: "primary", disabled: registerDisabled, icon: React.createElement(DownOutlined, null) })))) : (React.createElement(Button, { type: "primary", disabled: registerDisabled, onClick: () => setVisibleEdit(true), icon: React.createElement(UserAddOutlined, null) }, _T('team.register')));
    const footer = () => (React.createElement(Space, { wrap: true },
        RegisterButton,
        React.createElement(ButtonConfirm, { title: _T('common.delete.title', { name: _T('team.title').toLowerCase() }), description: _T('common.delete.content', { name: selectedRowKeys.length + ' ' + _T('team.title').toLowerCase() }), onConfirm: deleteSelectedTeams, buttonProps: {
                danger: true,
                disabled: disabled || selectedRowKeys.length === 0,
                icon: React.createElement(DeleteOutlined, null)
            } }),
        React.createElement(AssignStartNumbers, { competition_id: competition_id, classItem: classItem, disabled: disabled || selectedRowKeys.length > 0 }),
        React.createElement(ExternalAPI, { competition_id: competition_id, classItem: classItem, mode: 'team', disabled: disabled })));
    return (React.createElement(React.Fragment, null,
        React.createElement(Table, { rowSelection: rowSelection, columns: columns, dataSource: teams, pagination: false, loading: loading, size: "small", rowKey: "team_id", footer: footer }),
        React.createElement(TeamEdit, { competition_id: competition_id, teamItem: editedTeam, classItem: classItem, visible: visibleEdit, onClose: closeTeam, onDuplicate: duplicateTeam, disabled: disabled }),
        React.createElement(GenerateRandomTeams, { competition_id: competition_id, classItem: classItem, visible: visibleGenerate, onClose: () => setVisibleGenerate(false) })));
};
